/**
 * FmTopBarShell — the single owner of the `fm-top-bar` DOM/class contract.
 *
 * Both the authenticated app bar (FmTopBar) and the pre-auth marketing bar
 * (FmProductChrome) compose this shell so the `fm-top-bar` header → __in/__left/
 * __nav/__right structure and the portal nav-drawer live in ONE primitive.
 * Consumers supply slot content + per-bar testids/aria; the shell fixes every
 * structural className so neither consumer re-clones the topbar markup.
 *
 * The shell renders the SAME className strings and attribute shapes the prior
 * inline FmTopBar markup produced — this is a structural extraction, not a
 * visual change. Styling stays in FmTopBar.css (the DS topbar stylesheet).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAdaptiveNavTheme } from './useAdaptiveNavTheme';
import { FmThemeIsland } from './FmThemeProvider';
import {
  FmTopBarMeasuredHeightContext,
  useFmTopBarMeasuredStyle,
} from './FmTopBarMeasuredHeightContext';

/** The rendered top-bar header, so its portaled drawer can follow the bar's own state. */
const FmTopBarHeaderElementContext = React.createContext<HTMLElement | null>(null);

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export interface FmTopBarShellHeaderProps {
  /** Extra classes appended after the base `fm-top-bar` (e.g. `ant-layout-header`). */
  className?: string;
  /** Header `data-testid` (e.g. `fm-top-bar`, `landing-top-bar`). */
  testId?: string;
  /** `__left` slot: wordmark + hamburger. */
  left: React.ReactNode;
  /** `__nav` slot content (the row of `fm-top-bar__tab` buttons). */
  nav: React.ReactNode;
  /** `aria-label` for the `<nav>` element. */
  navAriaLabel: string;
  /** `__right` slot: actions/account. */
  right: React.ReactNode;
  /** Rendered after `__in` (e.g. a portal drawer or auth modal). */
  children?: React.ReactNode;
}

/**
 * The `fm-top-bar` header shell: `header.fm-top-bar > .fm-top-bar__in`
 * containing the left/nav/right slots, plus arbitrary trailing children.
 */
export const FmTopBarShellHeader: React.FC<FmTopBarShellHeaderProps> = ({
  className,
  testId,
  left,
  nav,
  navAriaLabel,
  right,
  children,
}) => {
  // Transparent adaptive nav: flip the bar's own
  // data-theme to match the band currently behind it so the DS tokens resolve to
  // light-on-dark over a dark hero/map and dark-on-light over light content. The
  // bar background stays transparent in both states — only token-driven colours
  // change.
  // Measure the bar's REAL bottom edge so the probe samples the band physically
  // behind it. Passing 0 fell back to the --fm-topbar-h token, which drifts from
  // the rendered height and (critically) lands the probe exactly on a nested
  // scroll container's clip boundary where the dark band is missed.
  const headerRef = useRef<HTMLElement>(null);
  const [probeOffset, setProbeOffset] = useState(0);
  const [measuredHeight, setMeasuredHeight] = useState<string>();
  const [themeScope, setThemeScope] = useState<HTMLElement | null>(null);
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    setHeaderElement(el);
    const scope = el.closest<HTMLElement>('[data-fm-topbar-scope]') ?? el.parentElement;
    if (!scope) return undefined;
    setThemeScope(scope);
    // Publish the real rendered height only to the shell that owns this bar.
    // Gallery/public/app headers may mount independently; a header unmount must
    // never erase another shell's clearance variables.
    let publishedHeight: number | undefined;
    const publishHeight = (height: number): void => {
      if (height === publishedHeight) return;
      publishedHeight = height;
      const value = `${height}px`;
      scope.style.setProperty('--fm-topbar-h-measured', value);
      scope.style.setProperty('--app-shell-overlay-safe-top', value);
      setMeasuredHeight(value);
    };
    const measure = (): void => {
      const rect = el.getBoundingClientRect();
      const height = Math.round(rect.height);
      // Hidden documents can temporarily report zero during mounting or HMR.
      // Preserve the last measurement (or the CSS fallback) until layout exists.
      if (height <= 0) return;
      setProbeOffset(Math.round(rect.bottom));
      publishHeight(height);
    };
    measure();
    // Track content-driven height changes (font load, nav wrap, responsive
    // control swaps) — resize alone misses these.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
      scope.style.removeProperty('--fm-topbar-h-measured');
      scope.style.removeProperty('--app-shell-overlay-safe-top');
    };
  }, []);
  const navTheme = useAdaptiveNavTheme({
    probeOffsetPx: probeOffset,
    enabled: true,
    scopeElement: themeScope,
  });
  return (
    <FmTopBarMeasuredHeightContext.Provider value={measuredHeight}>
      <FmTopBarHeaderElementContext.Provider value={headerElement}>
      <FmThemeIsland theme={navTheme}>
      <header
        ref={headerRef}
        className={cx('fm-top-bar', className)}
        data-testid={testId}
        data-fm-adaptive-nav={navTheme}
      >
        <div className="fm-top-bar__in">
          <div className="fm-top-bar__left">{left}</div>
          <nav className="fm-top-bar__nav" aria-label={navAriaLabel}>
            {nav}
          </nav>
          <div className="fm-top-bar__right">{right}</div>
        </div>
        {children}
      </header>
      </FmThemeIsland>
      </FmTopBarHeaderElementContext.Provider>
    </FmTopBarMeasuredHeightContext.Provider>
  );
};

export interface FmTopBarNavTabProps {
  testId?: string;
  /** Adds `fm-top-bar__tab--active` when true (current-page highlight). */
  active?: boolean;
  /** Adds `fm-top-bar__tab--ask` accent (the single accent tab). */
  accent?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

/**
 * `fm-top-bar__tab` — a single centered text nav tab in the `__nav` slot.
 * Owns the tab DOM/class contract so neither the authenticated bar nor the
 * pre-auth marketing bar re-clones the `fm-top-bar__tab` markup.
 */
export const FmTopBarNavTab: React.FC<FmTopBarNavTabProps> = ({
  testId,
  active,
  accent,
  onClick,
  children,
}) => (
  <button
    type="button"
    className={cx(
      'fm-top-bar__tab',
      active && 'fm-top-bar__tab--active',
      accent && 'fm-top-bar__tab--ask',
    )}
    data-testid={testId}
    aria-current={active ? 'page' : undefined}
    onClick={onClick}
  >
    {children}
  </button>
);

export interface FmTopBarWordmarkProps {
  testId?: string;
  onActivate: () => void;
  children: React.ReactNode;
}

/** `fm-top-bar__wordmark` — keyboard-activatable brand link. */
export const FmTopBarWordmark: React.FC<FmTopBarWordmarkProps> = ({
  testId,
  onActivate,
  children,
}) => (
  <button type="button" className="fm-top-bar__wordmark" data-testid={testId} onClick={onActivate}>
    {children}
  </button>
);

export interface FmTopBarHamburgerProps {
  testId?: string;
  open: boolean;
  ariaLabel: string;
  onToggle: () => void;
  children: React.ReactNode;
}

/** `fm-top-bar__hamburger` — tablet/mobile nav-drawer toggle. */
export const FmTopBarHamburger: React.FC<FmTopBarHamburgerProps> = ({
  testId,
  open,
  ariaLabel,
  onToggle,
  children,
}) => (
  <button
    type="button"
    className="fm-top-bar__hamburger"
    data-testid={testId}
    aria-label={ariaLabel}
    aria-expanded={open}
    onClick={onToggle}
  >
    {children}
  </button>
);

export interface FmTopBarNavDrawerProps {
  /** `__nav-backdrop` testid. */
  backdropTestId?: string;
  /** `__nav-drawer` testid (FmTopBar sets one; Landing leaves it unset). */
  drawerTestId?: string;
  /** Extra props forwarded onto the backdrop (e.g. onWheel). */
  backdropProps?: React.HTMLAttributes<HTMLDivElement>;
  onClose: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

/**
 * Portal `fm-top-bar__nav-backdrop` + `fm-top-bar__nav-drawer` overlay.
 * Mounted to `document.body`; consumers gate mounting on their own open state.
 */
export const FmTopBarNavDrawer: React.FC<FmTopBarNavDrawerProps> = ({
  backdropTestId,
  drawerTestId,
  backdropProps,
  onClose,
  ariaLabel,
  children,
}) => {
  const measuredStyle = useFmTopBarMeasuredStyle();
  const headerElement = React.useContext(FmTopBarHeaderElementContext);
  // The bar switches to the drawer by its own width (a container query in
  // FmTopBar.css), which the portaled drawer cannot query. Close the drawer once
  // the bar has room for its inline navigation and hides the toggle.
  useEffect(() => {
    if (!headerElement || typeof ResizeObserver === 'undefined') return undefined;
    const closeWhenToggleHidden = (): void => {
      const toggle = headerElement.querySelector<HTMLElement>('.fm-top-bar__hamburger');
      if (toggle && getComputedStyle(toggle).display === 'none') onClose();
    };
    const observer = new ResizeObserver(closeWhenToggleHidden);
    observer.observe(headerElement);
    return () => observer.disconnect();
  }, [headerElement, onClose]);
  return createPortal(
    <>
      <div
        className="fm-top-bar__nav-backdrop"
        data-testid={backdropTestId}
        role="presentation"
        onClick={onClose}
        {...backdropProps}
        style={{ ...measuredStyle, ...backdropProps?.style }}
      />
      <dialog
        className="fm-top-bar__nav-drawer"
        data-testid={drawerTestId}
        open
        aria-label={ariaLabel}
        style={measuredStyle}
      >
        {children}
      </dialog>
    </>,
    document.body,
  );
};

export interface FmTopBarDrawerItemProps {
  testId?: string;
  /** Adds `fm-top-bar__drawer-item--active` when true. */
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

/** `fm-top-bar__drawer-item` — a single tappable row inside the nav drawer. */
export const FmTopBarDrawerItem: React.FC<FmTopBarDrawerItemProps> = ({
  testId,
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    className={cx('fm-top-bar__drawer-item', active && 'fm-top-bar__drawer-item--active')}
    data-testid={testId}
    onClick={onClick}
  >
    {children}
  </button>
);

/** `fm-top-bar__language-wrap` — wraps a language control in the actions cluster. */
export const FmTopBarLanguageWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="fm-top-bar__language-wrap">{children}</span>
);

export interface FmTopBarDrawerSearchProps {
  testId?: string;
  onClick: () => void;
  children: React.ReactNode;
}

/** `fm-top-bar__drawer-search` — the bottom search entry row in the nav drawer. */
export const FmTopBarDrawerSearch: React.FC<FmTopBarDrawerSearchProps> = ({
  testId,
  onClick,
  children,
}) => (
  <button
    type="button"
    className="fm-top-bar__drawer-search"
    data-testid={testId}
    onClick={onClick}
  >
    {children}
  </button>
);
